import React, { useEffect, useState } from 'react'
import { Container, makeStyles } from '@material-ui/core'
import { organizationStatus, subjectReason, SubjectStatus } from 'constants/index'
import { Box } from '@mui/material'
import PageNotFound from 'components/PageNotFound'
import ThankYou from 'components/ThankYou'
import { toast } from 'utils/notify'
import moment from 'moment'
import { getStatusBasedSubTitle } from 'utils'
import PublicOrganizationForm from './PublicOrganizationForm'
import LogoWithConference from 'components/LogoWithConference'
import useValidateOrganizationToken from 'hooks/useValidateOrganizationToken'
import { cloudFunctionsInstance } from 'config/axios'
import DataLoading from 'components/DataLoading'

const Public = () => {
  const classes = useStyles()
  const { isLoading, isTokenValid, organization, conference, refetch } =
    useValidateOrganizationToken()

  const [isFormSubmitted, setIsFormSubmitted] = useState(false)
  const [showForm, setShowForm] = useState(false)

  useEffect(() => {
    if (
      (!isFormSubmitted && organization?.status === organizationStatus.AWAITING_RESPONSE) ||
      (organization?.updateInfoNotificationSent && !organization?.submittedAt)
    ) {
      setShowForm(true)
    }
  }, [organization?.status])

  const handleEditSubmittedForm = async () => {
    setIsFormSubmitted(false)
    setShowForm(true)
    refetch && refetch()
  }

  const handleSubmitSubject = async (values) => {
    const nowDateTime = moment().utc().valueOf()
    const payload = {
      organization: values.organization.trim(),
      booth: values?.booth ? values?.booth.trim() : '',
      websiteUrl: values.websiteUrl.trim(),
      bannerClickUrl: values.bannerClickUrl.trim(),
      introduction: values.introduction.trim(),
      message: values.message.trim(),
      deleteLogoImageName: values.logoImageNameDeleted,
      deleteAdImageName: values.adImageNameDeleted,
      deleteBrochureImageName: values.brochureImageNameDeleted,
      status: SubjectStatus.PENDING_DATA_REVIEW,
      submittedAt: nowDateTime
    }

    try {
      const form = new FormData()

      form.append('organizationId', organization?.id)
      form.append('conferenceId', conference?.id)
      form.append('payloadData', JSON.stringify(payload))
      values?.logoImageName && form.append('logoImageName', values.logoImageName)
      values?.adImageName && form.append('adImageName', values.adImageName)
      values?.brochureImageName && form.append('brochureImageName', values.brochureImageName)
      await cloudFunctionsInstance.post('/submitOrganizationProfile', form)
      setIsFormSubmitted(true)
      setShowForm(false)
      toast.success('Data submitted successfully.')
    } catch (err) {
      console.error(err)
      toast.error(err?.response?.data?.message || 'Form submission failed. Please try again.')
    }
  }

  if (isLoading)
    return (
      <Box sx={{ mt: '2rem' }}>
        <DataLoading />
      </Box>
    )
  else if (!isTokenValid) return <PageNotFound />
  else if (isFormSubmitted && !showForm)
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle="Thank you for submitting the requested form. We appreciate your cooperation. If you want to make any changes, please click the button below."
      />
    )
  else if (
    !showForm &&
    (organization?.status === organizationStatus.APPROVED ||
      organization?.status === organizationStatus.PENDING_DATA_REVIEW)
  )
    return (
      <ThankYou
        onAction={handleEditSubmittedForm}
        subTitle={getStatusBasedSubTitle(organization?.status)}
      />
    )
  else if (!isFormSubmitted && showForm)
    return (
      <Box className={classes.pageScroll}>
        <Container maxWidth="xl" className={classes.container}>
          <LogoWithConference confName={conference?.confName} />
          <PublicOrganizationForm
            organization={organization}
            handleSubmit={handleSubmitSubject}
            submitTitle="Submit Your Information"
            reason={subjectReason.registration}
          />
        </Container>
      </Box>
    )
  else return null
}
export default Public

const useStyles = makeStyles({
  pageScroll: {
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
      width: 6
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#cbd5e1',
      borderRadius: 6
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#94a3b8'
    }
  },
  container: {
    paddingBottom: '1.5rem',
    paddingTop: '1.5rem'
  }
})
